// Default translations for the customer-facing auth app.
//
// This file is the DEFAULT layer. Admins can override any key from
// /admin/translations; overrides are stored in the `translations` DB table
// and merged on top of these values at runtime (see LanguageContext.js).
//
// Keys are namespaced by page. Every key MUST exist in both `sl` and `en` —
// src/i18n/parity.test.js enforces this.

export const translations = {
  sl: {
    common: {
      appName: 'Zlata Renta',
      loading: 'Nalaganje...',
      save: 'Shrani',
      cancel: 'Prekliči',
      back: 'Nazaj',
      next: 'Naprej',
      yes: 'Da',
      no: 'Ne',
      close: 'Zapri',
    },
    errors: {
      INVALID_CREDENTIALS: 'Napačen e-poštni naslov ali geslo',
      EMAIL_IN_USE: 'Ta e-poštni naslov je že registriran',
      SESSION_EXPIRED: 'Seja je potekla. Prijavite se znova.',
      RATE_LIMITED: 'Preveč poskusov. Poskusite znova čez nekaj minut.',
      INVALID_TOKEN: 'Povezava je neveljavna ali je potekla',
      WEAK_PASSWORD: 'Geslo mora vsebovati vsaj 8 znakov',
      REQUIRED_FIELD: 'To polje je obvezno',
      INVALID_EMAIL: 'Neveljaven e-poštni naslov',
      SERVER_ERROR: 'Prišlo je do napake. Poskusite znova.',
      ACCOUNT_SUSPENDED: 'Vaš račun je začasno onemogočen',
      ACCOUNT_REJECTED: 'Preverjanje vašega računa je bilo zavrnjeno',
      INVALID_FIELD_FORMAT: 'Eno ali več polj ima neveljavno obliko',
    },
    login: {
      subtitle: 'Prijavite se v svoj račun',
      emailLabel: 'E-poštni naslov',
      emailPlaceholder: 'vas@email.com',
      passwordLabel: 'Geslo',
      forgotPassword: 'Pozabljeno geslo?',
      submitting: 'Prijavljanje...',
      submit: 'Prijava',
      noAccount: 'Še nimate računa?',
      createAccount: 'Ustvarite račun',
    },
    register: {
      subtitle: 'Ustvarite račun',
      sectionTitle: 'Podatki za prijavo',
      sectionDescription: 'Vnesite e-poštni naslov in ustvarite geslo. Podatke stranke boste izpolnili po prijavi.',
      emailLabel: 'E-poštni naslov',
      emailPlaceholder: 'vas@email.com',
      passwordLabel: 'Geslo',
      passwordPlaceholder: 'Vsaj 8 znakov',
      confirmPasswordLabel: 'Potrdite geslo',
      confirmPasswordPlaceholder: 'Ponovite geslo',
      passwordRequirementsTitle: 'Geslo mora vsebovati:',
      passwordRequirementMinLength: 'Vsaj 8 znakov',
      submitting: 'Registracija...',
      submit: 'Ustvari račun',
      haveAccount: 'Že imate račun?',
      signIn: 'Prijavite se',
      errorRequiredFields: 'Vsa polja so obvezna',
      errorInvalidEmail: 'Vnesite veljaven e-poštni naslov',
      errorWeakPassword: 'Geslo mora imeti vsaj 8 znakov',
      errorPasswordMismatch: 'Gesli se ne ujemata',
    },
    forgotPassword: {
      subtitle: 'Ponastavitev gesla',
      successMessage: 'Če račun s tem e-poštnim naslovom obstaja, boste prejeli povezavo za ponastavitev gesla.',
      emailLabel: 'E-poštni naslov',
      emailPlaceholder: 'vas@email.com',
      submitting: 'Pošiljanje...',
      submit: 'Pošlji povezavo za ponastavitev',
      backToLogin: 'Nazaj na prijavo',
    },
    resetPassword: {
      subtitle: 'Nastavite novo geslo',
      errorMissingToken: 'Manjka žeton za ponastavitev. Zahtevajte novo povezavo.',
      errorWeakPassword: 'Geslo mora vsebovati vsaj 8 znakov',
      errorPasswordMismatch: 'Gesli se ne ujemata',
      newPasswordLabel: 'Novo geslo',
      confirmPasswordLabel: 'Potrdite novo geslo',
      submitting: 'Shranjevanje...',
      submit: 'Ponastavi geslo',
      requestNewLink: 'Zahtevaj novo povezavo',
    },
    layout: {
      navDashboard: 'Nadzorna plošča',
      navProfile: 'Moj profil',
      navShop: 'Trgovina',
      logout: 'Odjava',
      copyright: '© {{year}} Zlata Renta. Vse pravice pridržane.',
      footerContact: 'Kontakt',
      footerTerms: 'Pogoji uporabe',
      footerPrivacy: 'Zasebnost',
    },
    dashboard: {
      welcomeTitle: 'Dobrodošli, {{name}}!',
      welcomeSubtitle: 'Upravljajte svoj račun in si oglejte svoje podatke',
      defaultUser: 'Uporabnik',
      statusVerified: '✓ Verificirano',
      statusPending: '⏳ V pregledu',
      statusIncomplete: '📝 Nedokončano',
      statusSuspended: '⚠️ Suspendirano',
      kycCtaTitle: 'Izpolnite podatke stranke',
      kycCtaText: 'Za dokončanje računa in možnost nakupa izpolnite obrazec "Podatki stranke".',
      kycCtaButton: 'Izpolni zdaj →',
      verifiedAccountLabel: 'Verificiran račun',
      quickActionsTitle: 'Hitre akcije',
      buyGoldTitle: 'Kupite zlato',
      buyGoldText: 'Obiščite trgovino in nakupujte s shranjenimi podatki',
      editProfileTitle: 'Uredi profil',
      editProfileText: 'Posodobite svoje osebne podatke in nastavitve',
      savedDataTitle: 'Vaši shranjeni podatki',
      savedDataDescription: 'Ti podatki se bodo samodejno izpolnili ob nakupu na zlatarenta.com',
      documentSectionTitle: '🪪 Dokument',
      typeLabel: 'Tip:',
      idDocTypeIdCard: 'Osebna izkaznica',
      idDocTypePassport: 'Potni list',
      numberLabel: 'Številka:',
      validUntilLabel: 'Velja do:',
      permanentLabel: 'Trajno',
      basicDataSectionTitle: '📋 Osnovni podatki',
      taxNumberLabel: 'Davčna:',
      emsoLabel: 'EMŠO:',
      addressLabel: 'Naslov:',
      citizenshipLabel: 'Državljanstvo:',
      statusSectionTitle: '🌍 Status',
      dualCitizenshipLabel: 'Dvojno državljanstvo:',
      sanctionsLabel: 'Sankcije:',
      pepLabel: 'PEP:',
      sourceOfFundsSectionTitle: '💰 Vir sredstev',
      howItWorksTitle: 'Kako deluje?',
      step1Title: 'Registrirajte se enkrat',
      step1Text: 'Vnesite svoje podatke za verifikacijo samo enkrat',
      step2Title: 'Nakupujte na zlatarenta.com',
      step2Text: 'Obiščite trgovino in izberite željene izdelke',
      step3Title: 'Prijavite se v košarici',
      step3Text: 'Vnesite email in geslo - podatki se samodejno izpolnijo',
      step4Title: 'Hitro zaključite nakup',
      step4Text: 'Brez ponovnega vnašanja podatkov!',
    },
    kyc: {
      legalForm: {
        doo: 'd.o.o. (družba z omejeno odgovornostjo)',
        sp: 's.p. (samostojni podjetnik)',
        dd: 'd.d. (delniška družba)',
        dno: 'd.n.o. (družba z neomejeno odgovornostjo)',
        kd: 'k.d. (komanditna družba)',
        zavod: 'Zavod',
        drustvo: 'Društvo',
      },
      idDocType: {
        osebna: 'Osebna izkaznica',
        potniList: 'Potni list',
      },
      purpose: {
        nalozba: 'Naložba / varčevanje',
        darilo: 'Darilo',
        zbirateljstvo: 'Zbirateljstvo',
        preprodaja: 'Preprodaja',
        zascitaPremozenja: 'Zaščita premoženja',
        ostalo: 'Drugo',
      },
      paymentMethod: {
        banka: 'Bančno nakazilo',
        kripto: 'Kriptovalute',
        gotovina: 'Gotovina',
        kombinacija: 'Kombinacija',
      },
      sourceOfFunds: {
        prihranki: 'Prihranki',
        placa: 'Plača',
        pokojnina: 'Pokojnina',
        studentskoDelo: 'Študentsko delo',
        honorar: 'Honorar',
        stipendija: 'Štipendija',
        regres: 'Regres',
        posel: 'Posel',
        prodajaNepremicnine: 'Prodaja nepremičnine',
        oddajaNepremicnine: 'Oddaja nepremičnine',
        prodajaVozila: 'Prodaja vozila',
        vrednostniPapirji: 'Vrednostni papirji',
        kripto: 'Kripto',
        varcevanjeZavarovanje: 'Varčevanje/zavarovanje',
        prodajaZlataKovin: 'Prodaja zlata oz. kovin',
        dedovanje: 'Dedovanje',
        ostalo: 'Ostalo',
      },
      howHeard: {
        splet: 'Splet / iskalnik',
        socialnaOmrezja: 'Socialna omrežja',
        priporocilo: 'Priporočilo',
        oglas: 'Oglas',
        mediji: 'Mediji',
        ostalo: 'Drugo',
      },
      bank: {
        placeholder: 'Izberite banko',
        other: 'Druga banka',
      },
      country: {
        slovenia: 'Slovenija',
        austria: 'Avstrija',
        belgium: 'Belgija',
        bulgaria: 'Bolgarija',
        bosniaAndHerzegovina: 'Bosna in Hercegovina',
        cyprus: 'Ciper',
        czechia: 'Češka',
        montenegro: 'Črna gora',
        denmark: 'Danska',
        estonia: 'Estonija',
        finland: 'Finska',
        france: 'Francija',
        greece: 'Grčija',
        croatia: 'Hrvaška',
        ireland: 'Irska',
        iceland: 'Islandija',
        italy: 'Italija',
        latvia: 'Latvija',
        liechtenstein: 'Lihtenštajn',
        lithuania: 'Litva',
        luxembourg: 'Luksemburg',
        hungary: 'Madžarska',
        malta: 'Malta',
        germany: 'Nemčija',
        netherlands: 'Nizozemska',
        norway: 'Norveška',
        poland: 'Poljska',
        portugal: 'Portugalska',
        romania: 'Romunija',
        northMacedonia: 'Severna Makedonija',
        slovakia: 'Slovaška',
        serbia: 'Srbija',
        spain: 'Španija',
        sweden: 'Švedska',
        switzerland: 'Švica',
        unitedKingdom: 'Velika Britanija',
        unitedStates: 'Združene države Amerike',
        japan: 'Japonska',
        uae: 'Združeni arabski emirati',
        turkey: 'Turčija',
        southAfrica: 'Južna Afrika',
        brazil: 'Brazilija',
        russia: 'Rusija',
        ukraine: 'Ukrajina',
        other: 'Drugo',
      },
      pepHelpText:
        'Politično izpostavljena oseba (PEP) je oseba, ki deluje ali je v zadnjem letu ' +
        'delovala na vidnem javnem položaju (npr. funkcionar, poslanec, sodnik, član ' +
        'uprave državnega podjetja), vključno z njihovimi ožjimi družinskimi člani in ' +
        'tesnimi sodelavci.',
    },
    verification: {
      common: {
        pageTitle: 'Podatki stranke',
        pageSubtitle: 'Izpolnite spodnje podatke za dokončanje vašega profila.',
        selectPlaceholder: 'Izberite',
        selectRegionPlaceholder: 'Izberite regijo',
        optionalMarker: '(neobvezno)',
        streetLabel: 'Ulica in hišna številka',
        postalCodeLabel: 'Poštna številka',
        cityLabel: 'Kraj',
        countryLabel: 'Država',
        birthDateLabel: 'Datum rojstva',
        citizenshipLabel: 'Državljanstvo',
        dateFormatPlaceholder: 'dd/mm/llll',
        invalidDateError: 'Neveljaven datum (dd/mm/llll)',
        futureDateError: 'Datum ne sme biti v prihodnosti',
        invalidPostalCodeError: 'Poštna številka mora vsebovati 4 številke',
        fixHighlightedFieldsError: 'Prosimo, popravite označena polja.',
        saveError: 'Napaka pri shranjevanju',
        uploadError: 'Napaka pri nalaganju datoteke',
        uploadingMessage: 'Nalaganje...',
        uploadedMessage: 'Datoteka "{{filename}}" naložena.',
      },
      step1: {
        title: 'Subjekt in identiteta',
        legalEntityQuestion: 'Ali ste pravna oseba?',
        individualOption: 'Fizična oseba',
        legalEntityOption: 'Pravna oseba',
        firstNameLabel: 'Ime',
        lastNameLabel: 'Priimek',
        companyNameLabel: 'Naziv podjetja',
        legalFormLabel: 'Pravna oblika',
        companyTaxNumberLabel: 'Davčna / VAT številka podjetja',
        registrationNumberLabel: 'Matična številka',
        registerCountryLabel: 'Država registracije',
        registerExtractLabel: 'Naložite izpisek iz registra',
        beneficialOwnersHeading: 'Dejanski lastniki',
        ownerFullNameLabel: 'Ime in priimek',
        ownershipShareLabel: 'Delež (%)',
        ownerAddressLabel: 'Naslov',
        removeOwnerButton: 'Odstrani',
        addOwnerButton: '+ Dodaj lastnika',
        phoneLabel: 'Telefon',
        invalidTaxNumberError: 'Neveljavna davčna/VAT številka',
        invalidRegistrationNumberError: 'Neveljavna matična številka (7 ali 10 številk)',
      },
      step2: {
        title: 'Naslov in rojstvo',
        permanentAddressHeading: 'Stalno prebivališče',
        regionLabel: 'Regija',
        regionPlaceholder: 'Vnesite regijo',
        hasTempAddressLabel: 'Imam začasno prebivališče',
        tempAddressHeading: 'Začasno prebivališče',
        birthTaxHeading: 'Rojstvo in davčna številka',
        birthplaceLabel: 'Kraj rojstva',
        birthCountryLabel: 'Država rojstva',
        taxNumberLabel: 'Davčna številka',
        personalIdLabel: 'EMŠO / JMBG',
        personalIdPlaceholder: '13 številk',
        invalidTaxNumberError: 'Neveljavna davčna številka (8 številk)',
        invalidPersonalIdError: 'Neveljavna osebna identifikacijska številka',
      },
      step3: {
        indicatorTitle: 'Dokument in PEP',
        title: 'Preverjanje identitete in dokument',
        identityVerificationHeading: 'Preverjanje identitete (Shufti)',
        identityVerifiedMessage: 'Identiteta uspešno preverjena.',
        dualCitizenshipLabel: 'Imam dvojno državljanstvo',
        dualCitizenshipCountryLabel: 'Država drugega državljanstva',
        sanctionsConnectionLabel: 'Sem povezan z državami na seznamu za sankcije',
        sanctionsLinkText: 'Seznam omejevalnih ukrepov (gov.si)',
        idDocumentHeading: 'Identifikacijski dokument',
        idDocTypeLabel: 'Tip dokumenta',
        idDocNumberLabel: 'Številka dokumenta',
        idDocIssuerLabel: 'Izdajatelj',
        idDocIssuerPlaceholder: 'npr. UE Ljubljana',
        issueDateLabel: 'Datum izdaje',
        expiryDateLabel: 'Datum poteka',
        permanentDocLabel: 'Trajno (brez datuma poteka)',
        pepStatusHeading: 'PEP status',
        pepStatusLabel: 'Sem politično izpostavljena oseba (ali ožji član / sodelavec)',
        hidePepHelp: 'Skrij',
        whatIsPepButton: 'Kaj je PEP?',
        pepDetailsLabel: 'Podrobnosti PEP statusa',
        expiryDateRequiredError: 'Datum poteka je obvezen (ali označite "trajno")',
      },
      step4: {
        title: 'Kontekst transakcije',
        buyVolumeLabel: 'Predvideni letni obseg nakupov (EUR)',
        buyVolumePlaceholder: 'npr. 10000',
        purposeLabel: 'Namen',
        purposeOtherLabel: 'Opišite namen',
        sourceOfFundsHeading: 'Vir sredstev',
        sourceOfFundsOtherLabel: 'Opišite ostale vire sredstev',
        sourceProofLabel: 'Dokazilo o izvoru sredstev',
        sourceProofRequiredError: 'Dokazilo o izvoru sredstev je obvezno',
        paymentMethodLabel: 'Način plačila',
        recommendationLabel: 'Priporočilo od (neobvezno)',
        howHeardLabel: 'Kako ste izvedeli za nas?',
        howHeardOtherLabel: 'Opišite',
        commentLabel: 'Opomba (neobvezno)',
        invalidAmountError: 'Vnesite veljaven znesek',
        sourceOfFundsRequiredError: 'Izberite vsaj en vir sredstev',
        savingButton: 'Shranjevanje...',
        submitButton: 'Oddaj podatke',
      },
    },
    profile: {
      header: {
        title: 'Moj profil',
        subtitle: 'Upravljajte svoje podatke in nastavitve',
      },
      tabs: {
        personal: 'Osnovni podatki',
        document: 'Dokument',
        verification: 'Verifikacija',
        banking: 'Bančni podatki',
        security: 'Varnost',
      },
      common: {
        selectPlaceholder: 'Izberite',
      },
      actions: {
        save: 'Shrani spremembe',
        saving: 'Shranjevanje...',
      },
      personal: {
        heading: 'Osnovni podatki',
        emailLabel: 'E-poštni naslov',
        emailHint: 'E-poštni naslov ni mogoče spremeniti',
        firstNameLabel: 'Ime',
        lastNameLabel: 'Priimek',
        companyNameLabel: 'Naziv podjetja',
        legalFormLabel: 'Pravna oblika',
        companyTaxNumberLabel: 'Davčna / VAT številka podjetja',
        companyTaxNumberError: 'Neveljavna davčna/VAT številka',
        registrationNumberLabel: 'Matična številka',
        phoneLabel: 'Telefon',
        taxNumberLabel: 'Davčna številka',
        personalIdLabel: 'EMŠO / JMBG',
        personalIdPlaceholder: '13 številk',
        nationalIdError: 'Neveljavna osebna identifikacijska številka',
        birthDateLabel: 'Datum rojstva',
        birthplaceLabel: 'Kraj rojstva',
      },
      address: {
        heading: 'Naslov stalnega prebivališča',
        streetLabel: 'Ulica in hišna številka',
        postalCodeLabel: 'Poštna številka',
        cityLabel: 'Kraj',
        countryLabel: 'Država',
        regionLabel: 'Regija',
        selectRegionPlaceholder: 'Izberite regijo',
        regionPlaceholder: 'Vnesite regijo',
      },
      document: {
        heading: 'Identifikacijski dokument',
        typeLabel: 'Tip dokumenta',
        selectTypePlaceholder: 'Izberite tip dokumenta',
        numberLabel: 'Številka dokumenta',
        issuerLabel: 'Izdajatelj',
        issueDateLabel: 'Datum izdaje',
        expiryDateLabel: 'Datum poteka',
        permanentLabel: 'Trajno (brez datuma poteka)',
      },
      verification: {
        heading: 'Verifikacijski podatki',
        citizenshipHeading: 'Državljanstvo in sankcije',
        citizenshipLabel: 'Državljanstvo',
        dualCitizenshipLabel: 'Dvojno državljanstvo',
        dualCitizenshipCountryLabel: 'Država drugega državljanstva',
        sanctionsLabel: 'Povezava z državami na seznamu za sankcije',
        pepLabel: 'PEP (politično izpostavljena oseba)',
        pepDetailsLabel: 'Podrobnosti PEP statusa',
        transactionHeading: 'Kontekst transakcije',
        buyVolumeLabel: 'Predvideni letni obseg nakupov (EUR)',
        purposeLabel: 'Namen',
        paymentMethodLabel: 'Način plačila',
        sourceOfFundsHeading: 'Vir sredstev',
        sourceOfFundsOtherLabel: 'Opišite ostale vire sredstev',
      },
      banking: {
        heading: 'Bančni podatki',
        description: 'Ti podatki so potrebni za SEPA nakupe.',
        ibanLabel: 'IBAN',
        bicLabel: 'BIC / SWIFT',
        bankNameLabel: 'Ime banke',
        customBankNameLabel: 'Ime druge banke',
      },
      security: {
        heading: 'Varnost',
        currentPasswordLabel: 'Trenutno geslo',
        newPasswordLabel: 'Novo geslo',
        newPasswordPlaceholder: 'Vsaj 8 znakov',
        confirmPasswordLabel: 'Ponovite novo geslo',
        confirmPasswordPlaceholder: 'Ponovite geslo',
        changePassword: 'Spremeni geslo',
        changing: 'Spreminjanje...',
      },
      messages: {
        saveSuccess: 'Profil je bil uspešno posodobljen!',
        saveError: 'Napaka pri shranjevanju',
        passwordMismatch: 'Novi gesli se ne ujemata',
        passwordTooShort: 'Novo geslo mora imeti vsaj 8 znakov',
        passwordChangeSuccess: 'Geslo je bilo uspešno spremenjeno!',
        passwordChangeError: 'Napaka pri spremembi gesla',
      },
    },
    shufti: {
      title: 'Preverjanje identitete',
      start: {
        description: 'Za registracijo potrebujemo preverjanje vaše identitete. Pripravite veljaven osebni dokument (osebno izkaznico ali potni list).',
        requirementId: 'Osebna izkaznica ali potni list',
        requirementSelfie: 'Selfie za preverjanje identitete',
        requirementLighting: 'Dobra osvetlitev',
        startButton: 'Začni preverjanje',
      },
      loading: {
        message: 'Pripravljam preverjanje identitete...',
      },
      verifying: {
        subtitle: 'Sledite navodilom za zaključek preverjanja',
        secureBadge: 'Varno',
        iframeTitle: 'Preverjanje Shufti Pro',
        secureFooter: '256-bit SSL šifriranje',
        cancelButton: 'Prekliči in začni znova',
      },
      polling: {
        title: 'Preverjanje podatkov...',
        message: 'Čakamo na potrditev vašega preverjanja. To lahko traja nekaj sekund.',
        delayedMessage: 'Preverjanje traja dlje kot običajno. Prosim počakajte...',
      },
      complete: {
        title: 'Preverjanje uspešno!',
        subtitle: 'Vaša identiteta je bila uspešno potrjena. Podatki iz dokumenta:',
        firstNameLabel: 'Ime:',
        lastNameLabel: 'Priimek:',
        birthDateLabel: 'Datum rojstva:',
        idTypeLabel: 'Tip dokumenta:',
        idNumberLabel: 'Številka dokumenta:',
        idExpiryLabel: 'Veljavnost dokumenta:',
        note: 'Ti podatki bodo uporabljeni za izpolnitev obrazca. V naslednjem koraku jih lahko uredite.',
        continueButton: 'Nadaljuj z registracijo',
      },
      docType: {
        idCard: 'Osebna izkaznica',
        passport: 'Potni list',
      },
      failed: {
        title: 'Preverjanje ni uspelo',
        defaultMessage: 'Prišlo je do napake pri preverjanju vaše identitete.',
      },
      error: {
        title: 'Napaka',
        defaultMessage: 'Prišlo je do nepričakovane napake.',
      },
      common: {
        backButton: 'Nazaj',
        retryButton: 'Poskusi znova',
      },
      errors: {
        sessionExpired: 'Seja za preverjanje je potekla. Prosim, začnite znova.',
        verificationTimeout: 'Rezultatov preverjanja ni bilo mogoče pridobiti. Prosim, poskusite znova.',
        verificationFailed: 'Preverjanje identitete ni uspelo. Prosim, poskusite znova.',
        verificationCancelled: 'Preverjanje identitete ni uspelo ali je bilo preklicano.',
        startFailed: 'Zagon preverjanja ni uspel.',
        statusCheckFailed: 'Preverjanje stanja ni uspelo.',
      },
    },
  },
  en: {
    common: {
      appName: 'Zlata Renta',
      loading: 'Loading...',
      save: 'Save',
      cancel: 'Cancel',
      back: 'Back',
      next: 'Next',
      yes: 'Yes',
      no: 'No',
      close: 'Close',
    },
    errors: {
      INVALID_CREDENTIALS: 'Incorrect email address or password',
      EMAIL_IN_USE: 'This email address is already registered',
      SESSION_EXPIRED: 'Your session has expired. Please sign in again.',
      RATE_LIMITED: 'Too many attempts. Please try again in a few minutes.',
      INVALID_TOKEN: 'This link is invalid or has expired',
      WEAK_PASSWORD: 'Password must be at least 8 characters',
      REQUIRED_FIELD: 'This field is required',
      INVALID_EMAIL: 'Invalid email address',
      SERVER_ERROR: 'Something went wrong. Please try again.',
      ACCOUNT_SUSPENDED: 'Your account has been suspended',
      ACCOUNT_REJECTED: 'Your account verification was rejected',
      INVALID_FIELD_FORMAT: 'One or more fields have an invalid format',
    },
    login: {
      subtitle: 'Sign in to your account',
      emailLabel: 'Email address',
      emailPlaceholder: 'you@email.com',
      passwordLabel: 'Password',
      forgotPassword: 'Forgot your password?',
      submitting: 'Signing in...',
      submit: 'Sign in',
      noAccount: "Don't have an account?",
      createAccount: 'Create an account',
    },
    register: {
      subtitle: 'Create an account',
      sectionTitle: 'Sign-in details',
      sectionDescription: 'Enter your email address and create a password. You will complete your customer details after signing up.',
      emailLabel: 'Email address',
      emailPlaceholder: 'you@email.com',
      passwordLabel: 'Password',
      passwordPlaceholder: 'At least 8 characters',
      confirmPasswordLabel: 'Confirm password',
      confirmPasswordPlaceholder: 'Repeat password',
      passwordRequirementsTitle: 'Your password must contain:',
      passwordRequirementMinLength: 'At least 8 characters',
      submitting: 'Creating account...',
      submit: 'Create account',
      haveAccount: 'Already have an account?',
      signIn: 'Sign in',
      errorRequiredFields: 'All fields are required',
      errorInvalidEmail: 'Enter a valid email address',
      errorWeakPassword: 'Password must be at least 8 characters',
      errorPasswordMismatch: 'Passwords do not match',
    },
    forgotPassword: {
      subtitle: 'Reset your password',
      successMessage: 'If an account with that email address exists, you will receive a password reset link.',
      emailLabel: 'Email address',
      emailPlaceholder: 'you@email.com',
      submitting: 'Sending...',
      submit: 'Send reset link',
      backToLogin: 'Back to sign in',
    },
    resetPassword: {
      subtitle: 'Set a new password',
      errorMissingToken: 'Reset token is missing. Please request a new link.',
      errorWeakPassword: 'Password must be at least 8 characters',
      errorPasswordMismatch: 'Passwords do not match',
      newPasswordLabel: 'New password',
      confirmPasswordLabel: 'Confirm new password',
      submitting: 'Saving...',
      submit: 'Reset password',
      requestNewLink: 'Request a new link',
    },
    layout: {
      navDashboard: 'Dashboard',
      navProfile: 'My profile',
      navShop: 'Shop',
      logout: 'Sign out',
      copyright: '© {{year}} Zlata Renta. All rights reserved.',
      footerContact: 'Contact',
      footerTerms: 'Terms of service',
      footerPrivacy: 'Privacy',
    },
    dashboard: {
      welcomeTitle: 'Welcome, {{name}}!',
      welcomeSubtitle: 'Manage your account and view your details',
      defaultUser: 'User',
      statusVerified: '✓ Verified',
      statusPending: '⏳ Under review',
      statusIncomplete: '📝 Incomplete',
      statusSuspended: '⚠️ Suspended',
      kycCtaTitle: 'Complete your customer details',
      kycCtaText: 'To finish setting up your account and be able to purchase, complete the "Customer details" form.',
      kycCtaButton: 'Complete now →',
      verifiedAccountLabel: 'Verified account',
      quickActionsTitle: 'Quick actions',
      buyGoldTitle: 'Buy gold',
      buyGoldText: 'Visit the shop and check out with your saved details',
      editProfileTitle: 'Edit profile',
      editProfileText: 'Update your personal details and settings',
      savedDataTitle: 'Your saved details',
      savedDataDescription: 'This information will automatically fill in when you check out on zlatarenta.com',
      documentSectionTitle: '🪪 Document',
      typeLabel: 'Type:',
      idDocTypeIdCard: 'ID card',
      idDocTypePassport: 'Passport',
      numberLabel: 'Number:',
      validUntilLabel: 'Valid until:',
      permanentLabel: 'Permanent',
      basicDataSectionTitle: '📋 Basic details',
      taxNumberLabel: 'Tax number:',
      emsoLabel: 'EMŠO:',
      addressLabel: 'Address:',
      citizenshipLabel: 'Citizenship:',
      statusSectionTitle: '🌍 Status',
      dualCitizenshipLabel: 'Dual citizenship:',
      sanctionsLabel: 'Sanctions:',
      pepLabel: 'PEP:',
      sourceOfFundsSectionTitle: '💰 Source of funds',
      howItWorksTitle: 'How it works',
      step1Title: 'Sign up once',
      step1Text: 'Enter your verification details just once',
      step2Title: 'Shop at zlatarenta.com',
      step2Text: 'Visit the shop and choose the products you want',
      step3Title: 'Sign in at checkout',
      step3Text: 'Enter your email and password - your details fill in automatically',
      step4Title: 'Check out in seconds',
      step4Text: 'No need to re-enter your details!',
    },
    kyc: {
      legalForm: {
        doo: 'd.o.o. (limited liability company)',
        sp: 's.p. (sole proprietor)',
        dd: 'd.d. (public limited company)',
        dno: 'd.n.o. (unlimited partnership)',
        kd: 'k.d. (limited partnership)',
        zavod: 'Institute',
        drustvo: 'Association',
      },
      idDocType: {
        osebna: 'ID card',
        potniList: 'Passport',
      },
      purpose: {
        nalozba: 'Investment / savings',
        darilo: 'Gift',
        zbirateljstvo: 'Collecting',
        preprodaja: 'Resale',
        zascitaPremozenja: 'Asset protection',
        ostalo: 'Other',
      },
      paymentMethod: {
        banka: 'Bank transfer',
        kripto: 'Cryptocurrency',
        gotovina: 'Cash',
        kombinacija: 'Combination',
      },
      sourceOfFunds: {
        prihranki: 'Savings',
        placa: 'Salary',
        pokojnina: 'Pension',
        studentskoDelo: 'Student work',
        honorar: 'Freelance income',
        stipendija: 'Scholarship',
        regres: 'Holiday allowance',
        posel: 'Business',
        prodajaNepremicnine: 'Sale of real estate',
        oddajaNepremicnine: 'Renting out real estate',
        prodajaVozila: 'Sale of a vehicle',
        vrednostniPapirji: 'Securities',
        kripto: 'Crypto',
        varcevanjeZavarovanje: 'Savings / insurance',
        prodajaZlataKovin: 'Sale of gold or other metals',
        dedovanje: 'Inheritance',
        ostalo: 'Other',
      },
      howHeard: {
        splet: 'Web / search engine',
        socialnaOmrezja: 'Social media',
        priporocilo: 'Recommendation',
        oglas: 'Advertisement',
        mediji: 'Media',
        ostalo: 'Other',
      },
      bank: {
        placeholder: 'Select a bank',
        other: 'Other bank',
      },
      country: {
        slovenia: 'Slovenia',
        austria: 'Austria',
        belgium: 'Belgium',
        bulgaria: 'Bulgaria',
        bosniaAndHerzegovina: 'Bosnia and Herzegovina',
        cyprus: 'Cyprus',
        czechia: 'Czechia',
        montenegro: 'Montenegro',
        denmark: 'Denmark',
        estonia: 'Estonia',
        finland: 'Finland',
        france: 'France',
        greece: 'Greece',
        croatia: 'Croatia',
        ireland: 'Ireland',
        iceland: 'Iceland',
        italy: 'Italy',
        latvia: 'Latvia',
        liechtenstein: 'Liechtenstein',
        lithuania: 'Lithuania',
        luxembourg: 'Luxembourg',
        hungary: 'Hungary',
        malta: 'Malta',
        germany: 'Germany',
        netherlands: 'Netherlands',
        norway: 'Norway',
        poland: 'Poland',
        portugal: 'Portugal',
        romania: 'Romania',
        northMacedonia: 'North Macedonia',
        slovakia: 'Slovakia',
        serbia: 'Serbia',
        spain: 'Spain',
        sweden: 'Sweden',
        switzerland: 'Switzerland',
        unitedKingdom: 'United Kingdom',
        unitedStates: 'United States',
        japan: 'Japan',
        uae: 'United Arab Emirates',
        turkey: 'Turkey',
        southAfrica: 'South Africa',
        brazil: 'Brazil',
        russia: 'Russia',
        ukraine: 'Ukraine',
        other: 'Other',
      },
      pepHelpText:
        'A politically exposed person (PEP) is someone who holds, or held within ' +
        'the past year, a prominent public position (e.g. an official, member of ' +
        'parliament, judge, or executive of a state-owned company), including their ' +
        'immediate family members and close associates.',
    },
    verification: {
      common: {
        pageTitle: 'Customer details',
        pageSubtitle: 'Complete the details below to finish setting up your profile.',
        selectPlaceholder: 'Select',
        selectRegionPlaceholder: 'Select a region',
        optionalMarker: '(optional)',
        streetLabel: 'Street and house number',
        postalCodeLabel: 'Postal code',
        cityLabel: 'City',
        countryLabel: 'Country',
        birthDateLabel: 'Date of birth',
        citizenshipLabel: 'Citizenship',
        dateFormatPlaceholder: 'dd/mm/yyyy',
        invalidDateError: 'Invalid date (dd/mm/yyyy)',
        futureDateError: 'Date cannot be in the future',
        invalidPostalCodeError: 'Postal code must contain 4 digits',
        fixHighlightedFieldsError: 'Please correct the highlighted fields.',
        saveError: 'Error saving your details',
        uploadError: 'Error uploading the file',
        uploadingMessage: 'Uploading...',
        uploadedMessage: 'File "{{filename}}" uploaded.',
      },
      step1: {
        title: 'Subject and identity',
        legalEntityQuestion: 'Are you a legal entity?',
        individualOption: 'Individual',
        legalEntityOption: 'Legal entity',
        firstNameLabel: 'First name',
        lastNameLabel: 'Last name',
        companyNameLabel: 'Company name',
        legalFormLabel: 'Legal form',
        companyTaxNumberLabel: 'Company tax / VAT number',
        registrationNumberLabel: 'Registration number',
        registerCountryLabel: 'Country of registration',
        registerExtractLabel: 'Upload a register extract',
        beneficialOwnersHeading: 'Beneficial owners',
        ownerFullNameLabel: 'Full name',
        ownershipShareLabel: 'Ownership share (%)',
        ownerAddressLabel: 'Address',
        removeOwnerButton: 'Remove',
        addOwnerButton: '+ Add owner',
        phoneLabel: 'Phone',
        invalidTaxNumberError: 'Invalid tax / VAT number',
        invalidRegistrationNumberError: 'Invalid registration number (7 or 10 digits)',
      },
      step2: {
        title: 'Address and date of birth',
        permanentAddressHeading: 'Permanent residence',
        regionLabel: 'Region',
        regionPlaceholder: 'Enter a region',
        hasTempAddressLabel: 'I have a temporary residence',
        tempAddressHeading: 'Temporary residence',
        birthTaxHeading: 'Date of birth and tax number',
        birthplaceLabel: 'Place of birth',
        birthCountryLabel: 'Country of birth',
        taxNumberLabel: 'Tax number',
        personalIdLabel: 'EMŠO / JMBG',
        personalIdPlaceholder: '13 digits',
        invalidTaxNumberError: 'Invalid tax number (8 digits)',
        invalidPersonalIdError: 'Invalid personal ID number',
      },
      step3: {
        indicatorTitle: 'Document & PEP',
        title: 'Identity verification and document',
        identityVerificationHeading: 'Identity verification (Shufti)',
        identityVerifiedMessage: 'Identity successfully verified.',
        dualCitizenshipLabel: 'I hold dual citizenship',
        dualCitizenshipCountryLabel: 'Country of second citizenship',
        sanctionsConnectionLabel: 'I am connected to countries on a sanctions list',
        sanctionsLinkText: 'List of restrictive measures (gov.si)',
        idDocumentHeading: 'Identification document',
        idDocTypeLabel: 'Document type',
        idDocNumberLabel: 'Document number',
        idDocIssuerLabel: 'Issuing authority',
        idDocIssuerPlaceholder: 'e.g. Ljubljana Administrative Unit',
        issueDateLabel: 'Date of issue',
        expiryDateLabel: 'Expiry date',
        permanentDocLabel: 'Permanent (no expiry date)',
        pepStatusHeading: 'PEP status',
        pepStatusLabel: 'I am a politically exposed person (or an immediate family member / close associate)',
        hidePepHelp: 'Hide',
        whatIsPepButton: 'What is a PEP?',
        pepDetailsLabel: 'PEP status details',
        expiryDateRequiredError: 'Expiry date is required (or mark as "permanent")',
      },
      step4: {
        title: 'Transaction context',
        buyVolumeLabel: 'Expected annual purchase volume (EUR)',
        buyVolumePlaceholder: 'e.g. 10000',
        purposeLabel: 'Purpose',
        purposeOtherLabel: 'Describe the purpose',
        sourceOfFundsHeading: 'Source of funds',
        sourceOfFundsOtherLabel: 'Describe other sources of funds',
        sourceProofLabel: 'Proof of source of funds',
        sourceProofRequiredError: 'Proof of source of funds is required',
        paymentMethodLabel: 'Payment method',
        recommendationLabel: 'Recommended by (optional)',
        howHeardLabel: 'How did you hear about us?',
        howHeardOtherLabel: 'Please describe',
        commentLabel: 'Note (optional)',
        invalidAmountError: 'Enter a valid amount',
        sourceOfFundsRequiredError: 'Select at least one source of funds',
        savingButton: 'Saving...',
        submitButton: 'Submit details',
      },
    },
    profile: {
      header: {
        title: 'My Profile',
        subtitle: 'Manage your details and settings',
      },
      tabs: {
        personal: 'Basic Info',
        document: 'Document',
        verification: 'Verification',
        banking: 'Banking Details',
        security: 'Security',
      },
      common: {
        selectPlaceholder: 'Select',
      },
      actions: {
        save: 'Save changes',
        saving: 'Saving...',
      },
      personal: {
        heading: 'Basic Info',
        emailLabel: 'Email address',
        emailHint: 'Your email address cannot be changed',
        firstNameLabel: 'First name',
        lastNameLabel: 'Last name',
        companyNameLabel: 'Company name',
        legalFormLabel: 'Legal form',
        companyTaxNumberLabel: 'Company tax / VAT number',
        companyTaxNumberError: 'Invalid tax/VAT number',
        registrationNumberLabel: 'Registration number',
        phoneLabel: 'Phone',
        taxNumberLabel: 'Tax number',
        personalIdLabel: 'EMŠO / JMBG',
        personalIdPlaceholder: '13 digits',
        nationalIdError: 'Invalid national identification number',
        birthDateLabel: 'Date of birth',
        birthplaceLabel: 'Place of birth',
      },
      address: {
        heading: 'Permanent residence address',
        streetLabel: 'Street and house number',
        postalCodeLabel: 'Postal code',
        cityLabel: 'City',
        countryLabel: 'Country',
        regionLabel: 'Region',
        selectRegionPlaceholder: 'Select a region',
        regionPlaceholder: 'Enter a region',
      },
      document: {
        heading: 'Identification document',
        typeLabel: 'Document type',
        selectTypePlaceholder: 'Select document type',
        numberLabel: 'Document number',
        issuerLabel: 'Issuer',
        issueDateLabel: 'Issue date',
        expiryDateLabel: 'Expiry date',
        permanentLabel: 'Permanent (no expiry date)',
      },
      verification: {
        heading: 'Verification details',
        citizenshipHeading: 'Citizenship and sanctions',
        citizenshipLabel: 'Citizenship',
        dualCitizenshipLabel: 'Dual citizenship',
        dualCitizenshipCountryLabel: 'Country of second citizenship',
        sanctionsLabel: 'Connection to sanctioned countries',
        pepLabel: 'PEP (politically exposed person)',
        pepDetailsLabel: 'PEP status details',
        transactionHeading: 'Transaction context',
        buyVolumeLabel: 'Expected annual purchase volume (EUR)',
        purposeLabel: 'Purpose',
        paymentMethodLabel: 'Payment method',
        sourceOfFundsHeading: 'Source of funds',
        sourceOfFundsOtherLabel: 'Describe other sources of funds',
      },
      banking: {
        heading: 'Banking details',
        description: 'This information is required for SEPA purchases.',
        ibanLabel: 'IBAN',
        bicLabel: 'BIC / SWIFT',
        bankNameLabel: 'Bank name',
        customBankNameLabel: 'Other bank name',
      },
      security: {
        heading: 'Security',
        currentPasswordLabel: 'Current password',
        newPasswordLabel: 'New password',
        newPasswordPlaceholder: 'At least 8 characters',
        confirmPasswordLabel: 'Repeat new password',
        confirmPasswordPlaceholder: 'Repeat password',
        changePassword: 'Change password',
        changing: 'Changing...',
      },
      messages: {
        saveSuccess: 'Profile updated successfully!',
        saveError: 'Error saving changes',
        passwordMismatch: 'The new passwords do not match',
        passwordTooShort: 'The new password must be at least 8 characters',
        passwordChangeSuccess: 'Password changed successfully!',
        passwordChangeError: 'Error changing password',
      },
    },
    shufti: {
      title: 'Identity verification',
      start: {
        description: 'To complete your registration, we need to verify your identity. Please have a valid photo ID ready (national ID card or passport).',
        requirementId: 'National ID card or passport',
        requirementSelfie: 'A selfie for identity verification',
        requirementLighting: 'Good lighting',
        startButton: 'Start verification',
      },
      loading: {
        message: 'Preparing identity verification...',
      },
      verifying: {
        subtitle: 'Follow the instructions to complete verification',
        secureBadge: 'Secure',
        iframeTitle: 'Shufti Pro Verification',
        secureFooter: '256-bit SSL encryption',
        cancelButton: 'Cancel and start over',
      },
      polling: {
        title: 'Checking your details...',
        message: 'We are waiting for your verification to be confirmed. This may take a few seconds.',
        delayedMessage: 'Verification is taking longer than usual. Please wait...',
      },
      complete: {
        title: 'Verification successful!',
        subtitle: 'Your identity has been successfully verified. Details from your document:',
        firstNameLabel: 'First name:',
        lastNameLabel: 'Last name:',
        birthDateLabel: 'Date of birth:',
        idTypeLabel: 'Document type:',
        idNumberLabel: 'Document number:',
        idExpiryLabel: 'Document valid until:',
        note: 'This information will be used to pre-fill the form. You can edit it in the next step.',
        continueButton: 'Continue with registration',
      },
      docType: {
        idCard: 'National ID card',
        passport: 'Passport',
      },
      failed: {
        title: 'Verification failed',
        defaultMessage: 'An error occurred while verifying your identity.',
      },
      error: {
        title: 'Error',
        defaultMessage: 'An unexpected error occurred.',
      },
      common: {
        backButton: 'Back',
        retryButton: 'Try again',
      },
      errors: {
        sessionExpired: 'Your verification session has expired. Please start again.',
        verificationTimeout: 'Could not retrieve verification results. Please try again.',
        verificationFailed: 'Identity verification failed. Please try again.',
        verificationCancelled: 'Identity verification failed or was cancelled.',
        startFailed: 'Failed to start verification.',
        statusCheckFailed: 'Failed to check verification status.',
      },
    },
  },
};

export default translations;
